import { useState } from 'react';
import { CasErrorDisplay, CasRenderBoolTrue, CasTable } from 'src/compass/shared/components';
import CasLoader from 'src/compass/shared/components/CasLoader/CasLoader';
import CasLink from 'src/compass/shared/components/CasLink/CasLink';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import getCasTableCellValue from 'src/compass/shared/components/CasTable/getCasTableCellValue';
import useRetrieveEntity from 'src/compass/shared/hooks/reactQuery/useRetrieveEntity';
import { YESNOALL_OPTIONS } from 'src/compass/application/general/components/CasYesNoSelect';
import { Terms } from 'src/compass/shared/util/terms';
import AccountDialog from './AccountDialog';

interface IProps {
  entityId?: number
  readOnly?: boolean;
};

export default function AccountsTab({ entityId, readOnly }: IProps) {
  const [dialogRowId, setDialogRowId] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState(new Date());

  const { data, isFetching, isError, refetch } = useRetrieveEntity({
    entity: 'Utilities/EntityAccount',
    params: { entityId },
    enabled: !!entityId,
  });

  const onClose = () => {
    refetch();
    setDialogRowId(null);
    setReloadTable(new Date());
  };

  const filters = [
    {
      name: 'search',
      label: 'Acct # or Name',
    },
    {
      type: 'select',
      name: 'unassignedOnly',
      label: 'Unassigned only?',
      options: YESNOALL_OPTIONS
    },
    {
      type: 'autocomplete',
      name: 'entityId',
      label: Terms.Customer.label,
      url: "Utilities/Entity/List",
    },
  ];

  const columns = [
    {
      field: 'company',
      label: 'Acct Name',
      render: ({ row, column }: ICasRenderCellProps) => {
        const value = getCasTableCellValue({ row, column });
        if(readOnly) return value;
        return (
          <CasLink onClick={() => setDialogRowId(row.id)}>
            {value}
          </CasLink>
        )
      },
    },
    {
      field: 'accountNumber',
      label: 'Account#',
    },
    ...(entityId
      ? []
      : [
          {
            field: 'entityName',
            label: Terms.Customer.label,
          },
        ]),
    {
      field: 'metaSourceType',
    },
    {
      field: 'isClosed',
      label: 'Closed',
      render: CasRenderBoolTrue,
    },
  ];

  const tableProps = entityId
    ? {
        data,
        columns,
      }
    : {
        update: reloadTable,
        entity: 'Utilities/EntityAccount',
        filters,
        columns,
        additionalPayload: { excludeOther: true },
      };

  if (isError) return <CasErrorDisplay message="Error retrieving data from the server." />;

  return (
    <>
      {isFetching && entityId ? <CasLoader /> : <CasTable {...tableProps} />}
      {dialogRowId && <AccountDialog id={dialogRowId} onClose={onClose} />}
    </>
  );
}
